package com.sddamico.draftmachine.ds

import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.textTransform
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.em

/**
 * Draft Machine — typography tokens. Full Kotlin translation of `tokens/typography.css`.
 * Display = Bungee, Heading = Bitter (bold only), Body/UI = Hanken Grotesk, Mono = Space Mono.
 * Families are loaded from Google Fonts (wired into <head> in build.gradle.kts).
 *
 * Use the `Modifier.role*()` helpers to apply a semantic text role in one call —
 * they mirror the `--role-*` CSS shorthands.
 */
object DmType {
  // Families (first match wins; brand face then platform fallbacks)
  val display = arrayOf("Bungee", "Arial Black", "Impact", "sans-serif")
  val heading = arrayOf("Bitter", "Georgia", "Times New Roman", "serif")
  val body = arrayOf("Hanken Grotesk", "system-ui", "-apple-system", "Segoe UI", "sans-serif")
  val mono = arrayOf("Space Mono", "ui-monospace", "SF Mono", "Menlo", "monospace")

  // Type scale (1.250 major-third-ish, tuned)
  val text2xs = 0.6875.cssRem // 11px — micro labels
  val textXs = 0.75.cssRem // 12px
  val textSm = 0.875.cssRem // 14px
  val textBase = 1.cssRem // 16px
  val textMd = 1.125.cssRem // 18px
  val textLg = 1.375.cssRem // 22px
  val textXl = 1.75.cssRem // 28px
  val text2xl = 2.25.cssRem // 36px
  val text3xl = 3.cssRem // 48px
  val text4xl = 4.cssRem // 64px
  val text5xl = 5.5.cssRem // 88px — hero poster

  // Weights
  const val weightRegular = 400
  const val weightMedium = 500
  const val weightSemibold = 600
  const val weightBold = 700
  const val weightBlack = 800

  // Line heights
  const val leadingTight = 1.04 // display
  const val leadingSnug = 1.2 // headings
  const val leadingNormal = 1.5 // body
  const val leadingRelaxed = 1.7 // long-form

  // Letter spacing
  val trackingTight = (-0.02).em
  val trackingNormal = 0.em
  val trackingWide = 0.04.em
  val trackingWider = 0.12.em // stamped uppercase labels
}

// ---- Semantic text roles (mirror --role-* shorthands) ----

fun Modifier.roleDisplay() = this
  .fontFamily(*DmType.display).fontSize(DmType.text4xl)
  .fontWeight(DmType.weightRegular).lineHeight(DmType.leadingTight)

fun Modifier.roleH1() = this
  .fontFamily(*DmType.heading).fontSize(DmType.text2xl)
  .fontWeight(DmType.weightBold).lineHeight(DmType.leadingSnug)

fun Modifier.roleH2() = this
  .fontFamily(*DmType.heading).fontSize(DmType.textXl)
  .fontWeight(DmType.weightBold).lineHeight(DmType.leadingSnug)

fun Modifier.roleH3() = this
  .fontFamily(*DmType.heading).fontSize(DmType.textLg)
  .fontWeight(DmType.weightBold).lineHeight(DmType.leadingSnug)

fun Modifier.roleBody() = this
  .fontFamily(*DmType.body).fontSize(DmType.textBase)
  .fontWeight(DmType.weightRegular).lineHeight(DmType.leadingNormal)

fun Modifier.roleSmall() = this
  .fontFamily(*DmType.body).fontSize(DmType.textSm)
  .fontWeight(DmType.weightRegular).lineHeight(DmType.leadingNormal)

fun Modifier.roleMono() = this
  .fontFamily(*DmType.mono).fontSize(DmType.textSm)
  .fontWeight(DmType.weightRegular).lineHeight(DmType.leadingNormal)

/**
 * The stamped uppercase micro-label — mono, 2xs, wide tracking, muted.
 * Mirrors the `.dm-stamp` utility class from `tokens/base.css`.
 */
fun Modifier.dmStamp() = this
  .fontFamily(*DmType.mono).fontSize(DmType.text2xs)
  .letterSpacing(DmType.trackingWider).textTransform(TextTransform.Uppercase)
  .color(DmColor.textMuted)
